/*
Site Video
- Shared IntersectionObservers (one for lazy-loading, one for visibility play/pause)
- Static instance registry for pauseOtherVideos
- Respects prefers-reduced-motion
*/

class SiteVideo extends HTMLElement {
  static instances = new Set();
  static _lazyObserver = null;
  static _visibilityObserver = null;
  static reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  static get lazyObserver() {
    if (!SiteVideo._lazyObserver) {
      SiteVideo._lazyObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const video = entry.target;
            if (video.readyState === 4) {
              SiteVideo._lazyObserver.unobserve(video);
              return;
            }
            Array.from(video.querySelectorAll("source[data-src]")).forEach(
              (source) => {
                source.src = source.dataset.src;
              },
            );
            video.load();
            if (video.hasAttribute("autoplay") && !SiteVideo.reducedMotion.matches) {
              video.play().catch(() => {});
            }
            SiteVideo._lazyObserver.unobserve(video);
          });
        },
        { rootMargin: "100% 0px" },
      );
    }
    return SiteVideo._lazyObserver;
  }

  static get visibilityObserver() {
    if (!SiteVideo._visibilityObserver) {
      SiteVideo._visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (!entry.isIntersecting) {
            video._wasPlaying = !video.paused;
            if (!video.paused) video.pause();
          } else if (video._wasPlaying || video.hasAttribute("autoplay")) {
            if (!SiteVideo.reducedMotion.matches) {
              video.play().catch(() => {});
            }
          }
        });
      });
    }
    return SiteVideo._visibilityObserver;
  }

  connectedCallback() {
    SiteVideo.instances.add(this);
    this.playBtn = this.querySelector(".play-button-js");
    this.video = this.querySelector("video");

    if (!this.video) return;

    if (SiteVideo.reducedMotion.matches) {
      this.video.removeAttribute("autoplay");
    }

    if (this.playBtn) {
      this.playBtn.addEventListener("click", () => {
        this.pauseOtherVideos();
        this.video.play();
      });

      this.video.addEventListener("click", () => {
        this.pauseOtherVideos();
        if (this.video.paused) {
          this.video.play();
        } else {
          this.video.pause();
        }
      });

      this.video.addEventListener("ended", () => {
        this.playBtn.classList.remove("hidden");
      });

      this.video.addEventListener("pause", () => {
        this.playBtn.classList.remove("hidden");
      });

      this.video.addEventListener("play", () => {
        this.playBtn.classList.add("hidden");
      });
    }

    if (this.video.querySelector("source[data-src]")) {
      SiteVideo.lazyObserver.observe(this.video);
    }

    if (this.dataset.stopOnObserve === "true") {
      SiteVideo.visibilityObserver.observe(this.video);
    }
  }

  disconnectedCallback() {
    SiteVideo.instances.delete(this);
    if (this.video) {
      SiteVideo._lazyObserver?.unobserve(this.video);
      SiteVideo._visibilityObserver?.unobserve(this.video);
    }
  }

  pauseOtherVideos() {
    SiteVideo.instances.forEach((instance) => {
      if (instance !== this && instance.video && !instance.video.paused) {
        instance.video.pause();
      }
    });
  }
}

customElements.define("site-video", SiteVideo);

/*
Carousel Prev
*/

class CarouselPrev extends HTMLElement {
  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("You need to have a child button to go to previous!");
    }

    this.addEventListener("click", (e) => {
      const carousels = e.target
        .closest("section")
        .querySelectorAll("site-carousel");
      if (carousels) {
        carousels.forEach((carousel) => {
          carousel.previous();
        });
      } else {
        console.error("<carousel-prev> needs to be a child of <site-carousel>");
      }
    });
  }
}

customElements.define("carousel-prev", CarouselPrev);

/*
Carousel Next
*/

class CarouselNext extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("You need to have a child button to go to next!");
    }

    this.addEventListener("click", (e) => {
      const carousels = e.target
        .closest("section")
        .querySelectorAll("site-carousel");
      if (carousels) {
        carousels.forEach((carousel) => {
          carousel.next();
        });
      } else {
        console.error("<carousel-next> needs to be a child of <site-carousel>");
      }
    });
  }
}

customElements.define("carousel-next", CarouselNext);

/*
Carousel Pager
*/

class CarouselPager extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.pagerButtons();
  }

  pagerButtons() {
    const pagerButtons = Array.from(this.querySelectorAll("button"));

    if (!pagerButtons.length > 1) {
      console.warn("No need for a pager with only one item!");
    }

    pagerButtons
      .filter((pager) => !pager.classList.contains("hidden"))
      .forEach((pager, i) => {
        pager.addEventListener("click", (e) => {
          const carousel = e.target.closest("site-carousel");

          if (carousel) {
            carousel.slideTo(i);
          } else {
            console.error(
              "<carousel-pager> needs to be a child of <site-carousel>"
            );
          }
        });
      });
  }
}

customElements.define("carousel-pager", CarouselPager);

/*
 * Site Carousel
 */

class SiteCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
  }

  connectedCallback() {
    const rawConfig = this.dataset.config
      ? JSON.parse(this.dataset.config)
      : { slidesPerView: 1, spaceBetween: 20, loop: true };

    let config = { ...rawConfig };

    this.swiper = new Swiper(`#${this.dataset.id} .swiper`, config);
    this.renderPagination();
    this.handleSlideChange();
    this.updateNavButtons();
    this.classList.remove("opacity-0");
  }

  renderPagination() {
    const el = this.querySelector(".swiper-pagination.custom-bullets");
    if (!el || !this.swiper) return;

    const total = this.swiper.snapGrid.length;
    el.innerHTML = "";

    for (let i = 0; i < total; i++) {
      const bullet = document.createElement("span");
      if (i === this.swiper.snapIndex) bullet.classList.add("active");
      bullet.addEventListener("click", () => this.swiper.slideTo(this.swiper.snapGrid[i] !== undefined ? i * (this.swiper.params.slidesPerGroup || 1) : i));
      el.appendChild(bullet);
    }
  }

  previous() {
    if (this.swiper) {
      this.swiper.slidePrev();
    }
  }

  next() {
    if (this.swiper) {
      this.swiper.slideNext();
    }
  }

  slideTo(i) {
    if (this.swiper) {
      this.swiper.slideTo(i);
    }
  }

  handleSlideChange() {
    const pagers = Array.from(this.querySelectorAll("carousel-pager"));
    const bulletContainer = this.querySelector(".swiper-pagination.custom-bullets");

    if (this.swiper) {
      this.swiper.on("slideChange", () => {
        const index = this.swiper.snapIndex;

        pagers.forEach((pager) => {
          const buttons = Array.from(pager.querySelectorAll("button")).filter(
            (button) => !button.classList.contains("hidden"),
          );
          if (buttons[index]) {
            buttons.forEach((button) => button.classList.remove("active"));
            buttons[index].classList.add("active");
          }
        });

        if (bulletContainer) {
          const bullets = bulletContainer.querySelectorAll("span");
          bullets.forEach((b) => b.classList.remove("active"));
          if (bullets[index]) bullets[index].classList.add("active");
        }

        this.updateNavButtons();
      });
    }
  }

  updateNavButtons() {
    const prevButtons = this.querySelectorAll("carousel-prev button");
    const nextButtons = this.querySelectorAll("carousel-next button");

    if (!this.swiper || this.swiper.params.loop) return;

    const isBeginning = this.swiper.isBeginning;
    const isEnd = this.swiper.isEnd;

    prevButtons.forEach((btn) => (btn.disabled = isBeginning));
    nextButtons.forEach((btn) => (btn.disabled = isEnd));
  }
}

customElements.define("site-carousel", SiteCarousel);

/*
Toggle Mobile Menu
*/

class ToggleMobileMenu extends HTMLElement {
  constructor() {
    super();
    this.header = null;
  }

  connectedCallback() {
    this.addEventListener("click", (e) => {
      const header = e.target.closest("site-header");
      const menu = header.querySelector(".mobile-menu");
      const cont = header.querySelector(".header-container");
      const siteHeader = header.querySelector(".site-header");

      header.classList.toggle("menu-active");
      menu.classList.toggle("opacity-100");
      menu.classList.toggle("visible");
      menu.classList.toggle("opacity-0");
      menu.classList.toggle("invisible");
      cont.classList.toggle("h-screen");
      cont.classList.toggle("bg-white");
      siteHeader.classList.toggle("text-white");
      siteHeader.classList.toggle("text-black");
    });
  }
}

customElements.define("toggle-mobile-menu", ToggleMobileMenu);

/*
Site Header
*/

class SiteHeader extends HTMLElement {
  constructor() {
    super();
    this.header = null;
  }

  connectedCallback() {
    this.header = this.querySelector(".site-header");
    this._ticking = false;
    window.addEventListener("scroll", () => {
      if (!this._ticking) {
        this._ticking = true;
        requestAnimationFrame(() => {
          this.handleScroll();
          this._ticking = false;
        });
      }
    });
    this.handleScroll();
  }

  handleScroll() {
    const scrollY = window.scrollY || window.pageYOffset;

    if (scrollY >= 32) {
      this.header.classList.add("active");
    } else {
      this.header.classList.remove("active");
    }
  }
}

customElements.define("site-header", SiteHeader);

/*
Cart Toggle
*/

class CartToggle extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("Cart toggle component should have a button as a child!");
    }

    this.addEventListener("click", (e) => {
      const cart = document.querySelector("site-cart");
      if (cart) {
        cart.toggleCart();
      }
    });
  }
}

customElements.define("cart-toggle", CartToggle);

/*
Cart Add
*/

class CartAdd extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.querySelector("form").addEventListener("submit", async (e) => {
      e.preventDefault();

      const $id = e.target.querySelector('[name="id"]');
      const $qty = e.target.querySelector('[name="quantity"]');
      const $sellingPlan = e.target.querySelector('[name="selling_plan"]');
      const qty = $qty ? $qty.value : 1;
      const sellingPlan =
        $sellingPlan && !!$sellingPlan.value ? $sellingPlan.value : null;

      const action = this.dataset.action || "cart";

      if ($id) {
        const cart = document.querySelector("site-cart");
        if (cart) {
          if (action === "cart") {
            cart.openCart();
          }
          await cart.addVariantToCart($id.value, qty, sellingPlan, false);
        }

        // TVSquared action tracking
        if (window.tvqTrackAction) {
          var productTitle = this.dataset.productTitle || '';
          var price = String((parseInt(this.dataset.productPrice, 10) || 0) / 100);

          window.tvqTrackAction('add_to_cart', {
            'rev': '0', 'prod': productTitle, 'id': String($id.value), 'promo': ''
          });
          if (action === 'checkout') {
            window.tvqTrackAction('checkout_started', {
              'rev': price, 'prod': productTitle, 'id': '', 'promo': ''
            });
          }
        }

        if (action === "checkout") {
          window.location.href = "/checkout";
        }
      }
    });
  }
}

customElements.define("cart-add", CartAdd);

/*
Update Quantity
*/

class UpdateQuantity extends HTMLElement {
  constructor() {
    super();
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  connectedCallback() {
    this.addEventListener(
      "click",
      this.debounce(async (e) => {
        const cart = document.querySelector("site-cart");
        const idx = this.dataset.index;
        const ctx = this.dataset.ctx;
        const val =
          this.parentElement.querySelector(".curr-quantity").innerHTML;
        let nextVal = Number(val);

        if (ctx === "decrement") nextVal--;
        else if (ctx === "increment") nextVal++;

        if (cart) {
          await cart.updateQuantity(idx, nextVal);
        }
      }, 300)
    );
  }
}

customElements.define("update-quantity", UpdateQuantity);


/*
Cart Remove
*/

class CartRemove extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", async (e) => {
      e.preventDefault();

      const cart = document.querySelector("site-cart");
      const idx = this.dataset.index;

      if (cart) {
        await cart.updateQuantity(idx, 0);
      }
    });
  }
}

customElements.define("cart-remove", CartRemove);

/*
Cart Member
*/

class CartMember extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", async (e) => {
      e.preventDefault();

      const button = e.target;
      const buttonText = button.innerHTML;
      const cart = document.querySelector("site-cart");

      button.innerHTML = "Adding...";

      if (cart) {
        await cart.addMemberProducts();
        button.innerHTML = buttonText;
      }
    });
  }
}

customElements.define("cart-member", CartMember);

/*
Site Cart
*/

class SiteCart extends HTMLElement {
  constructor() {
    super();
    this.cart = null;
    this.memberIds = this.dataset.memberIds;
    this.memberItems = this.dataset.memberItems ? JSON.parse(this.dataset.memberItems) : [];
    this.memberIdNums = this.memberIds ? this.memberIds.split(",").map(Number) : [];
    this.isCustomer = this.dataset.isCustomer;
  }

  connectedCallback() {
    this.cart = document.querySelector(".site-cart");
    this.checkUrlForCartOpen();
  }

  checkUrlForCartOpen() {
    const params = new URLSearchParams(window.location.search);
    if (params.get("cart_open") === "true") {
      this.openCart();
    }
  }

  openCart() {
    if (this.cart) {
      this.cart.classList.add("active");
    }
  }

  closeCart() {
    if (this.cart) {
      this.cart.classList.remove("active");
    }
  }

  toggleCart() {
    if (this.cart.classList.contains("active")) {
      this.closeCart();
    } else {
      this.openCart();
    }
  }

  getCartSections() {
    return [
      {
        section: "main-cart-items",
        selector: "#main-cart-items",
      },
      {
        section: "main-cart-footer",
        selector: "#main-cart-footer",
      },
      {
        section: "main-cart-header",
        selector: "#main-cart-header",
      },
      {
        section: "header",
        selector: "#header",
      },
    ];
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  renderCartSections(sections) {
    const getSections = this.getCartSections();
    const sectionsArr = Object.keys(sections);

    sectionsArr.forEach((sectionKey) => {
      const activeSection = getSections.find((s) => s.section === sectionKey);
      const elToReplace = document.querySelector(activeSection.selector);
      if (elToReplace) {
        elToReplace.innerHTML = this.getSectionInnerHTML(
          sections[sectionKey],
          activeSection.selector
        );
      }
    });
  }

  buildConfig(body) {
    return {
      body: body,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: `application/json`,
      },
    };
  }

  addVariantToCart(id, quantity = 1, sellingPlan, openCart = true) {
    if (openCart) {
      this.openCart();
    }

    const items = [
      sellingPlan
        ? { id, quantity, selling_plan: sellingPlan }
        : { id, quantity },
    ];
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ items, sections });
    const config = this.buildConfig(body);

    return fetch(`/cart/add`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.handleErrorMessage(response.description);
          return;
        }
        this.renderCartSections(response.sections);

        return response;
      })
      .catch((e) => {
        // handle error
      });
  }

  addVariantToCartWithSubscription(variantId, quantity = 1, planId, openCart) {
    const sellingPlanId = planId;
    console.log(variantId, quantity, sellingPlanId, openCart);

    return this.addVariantToCart(variantId, quantity, sellingPlanId, openCart);
  }

  addMemberProducts() {
    const items = this.memberItems;
    if (!items.length) return Promise.resolve();
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ items, sections });
    const config = this.buildConfig(body);

    return fetch(`/cart/add`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.handleErrorMessage(response.description);
          return;
        }
        this.renderCartSections(response.sections);
        this.updateMemberVisibility();
        this.openCart();

        return response;
      })
      .catch((e) => {
        // handle error
      });
  }

  getCart() {
    return fetch("/cart.js").then((response) => response.json());
  }

  updateQuantity(line, quantity) {
    const items = Array.from(document.querySelectorAll(".cart-item"));
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ line, quantity, sections });
    const config = this.buildConfig(body);
    const item = items[line - 1];
    const price = item.querySelector(".cart-price");
    const loader = item.querySelector(".cart-loader");

    if (price) {
      price.classList.add("opacity-0");
    }
    if (loader) {
      loader.classList.remove("opacity-0");
    }

    return fetch("/cart/change", config)
      .then((response) => response.json())
      .then(async (response) => {
        this.renderCartSections(response.sections);
        this.updateMemberVisibility();
        await this.removeMemberProductsIfNoBundleOrCustomer();

        if (price) {
          price.classList.remove("opacity-0");
        }
        if (loader) {
          loader.classList.add("opacity-0");
        }
      })
      .catch(() => {
        // handle error
      });
  }

  async removeMemberProductsIfNoBundleOrCustomer() {
    const cart = await this.fetchCart();
    const mappedItems = cart.items.map((item) => item.id);
    const hasMemberItems = this.memberIdNums.length > 0 &&
      this.memberIdNums.every((id) => mappedItems.includes(id));
    const memberIds = this.memberIds.split(",");
    const removedMemberIds = memberIds.reduce(
      (acc, id) => ({ ...acc, [id]: 0 }),
      {}
    );
    const isCustomer = this.isCustomer === "false" ? false : true;

    if (!hasMemberItems && !isCustomer) {
      const memberIds = this.memberIds.split(",");
      memberIds.forEach((id) => {
        const match = this.querySelector(`#cart-item-${id}`);
        if (match) {
          match.remove();
        }
      });
      this.updateCart(removedMemberIds);
    }
  }

  async updateMemberVisibility() {
    const cart = await this.fetchCart();
    const mappedItems = cart.items.map((item) => item.id);
    const hasMemberItems = this.memberIdNums.length > 0 &&
      this.memberIdNums.every((id) => mappedItems.includes(id));
    document.querySelectorAll(".for-members").forEach((member) => {
      if (hasMemberItems) {
        member.classList.remove("hidden");
      } else {
        member.classList.add("hidden");
      }
    });
    document.querySelectorAll(".for-non-members").forEach((nonmember) => {
      if (hasMemberItems) {
        nonmember.classList.add("hidden");
      } else {
        nonmember.classList.remove("hidden");
      }
    });
  }

  wipeCart() {
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ sections });
    const config = this.buildConfig(body);

    return fetch("/cart/clear", config)
      .then((response) => response.json())
      .then((response) => {
        this.renderCartSections(response.sections);
      })
      .catch(() => {
        // handle error
      });
  }

  updateCart(updates) {
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ updates, sections });
    const config = this.buildConfig(body);

    return fetch("/cart/update", config)
      .then((response) => response.json())
      .then((response) => {
        this.renderCartSections(response.sections);
      })
      .catch(() => {
        // handle error
      });
  }

  async fetchCart() {
    const uri = window.Shopify.routes.root + "cart.js";
    const resp = await fetch(uri, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    return await resp.json();
  }
}

customElements.define("site-cart", SiteCart);


/*
Number Input
*/

class NumberInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });

    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;
    const button = event.target.closest("button");

    if (button) {
      button.name === "increment" ? this.input.stepUp() : this.input.stepDown();

      if (previousValue !== this.input.value) {
        this.input.dispatchEvent(this.changeEvent);
      }
    }
  }
}

customElements.define("number-input", NumberInput);

/*
Klaviyo Signup
*/

class KlaviyoSignup extends HTMLElement {
  constructor() {
    super();
    this.context = this.dataset.context;
    this.listId = this.dataset.listId;
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    if (this.form) {
      this.form.addEventListener("submit", (e) => {
        e.preventDefault();
        this.submitForm(e);
      });
    }
  }

  async submitForm(e) {
    const error = this.querySelector(".error");
    const success = this.querySelector(".success");
    const emailOrPhone = this.form.querySelector('[type="text"]');
    const consent = this.form.querySelector('[type="checkbox"]');
    const submit = this.form.querySelector('[type="submit"]');
    const url = "https://feno-co-tjjv.vercel.app/api/subscribe";

    const fmtEmailOrPhone = emailOrPhone.value
      .replace("(", "")
      .replace(")", "")
      .replace("-", "")
      .replace(" ", "");
    const isAllNumbers = (str) => /^\d+$/.test(str);
    const isPhone = isAllNumbers(fmtEmailOrPhone);
    if (isPhone && !consent.checked == true) {
      error.innerHTML = "Please opt in to SMS to continue.";
      return;
    }

    error.innerHTML = "";

    const submitText = submit.innerHTML;
    const submitWidth = submit.getBoundingClientRect().width;
    submit.style.width = `${submitWidth}px`;
    submit.innerHTML = "...";

    try {
      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: `application/json`,
        },
        body: JSON.stringify({
          emailOrPhone: emailOrPhone.value,
          listId: this.listId,
        }),
      });
      const response = await resp.json();

      if (response.error) {
        throw new Error();
      }

      this.form.reset();
      submit.innerHTML = submitText;
      success.classList.remove("hidden");
      emailOrPhone.blur();
    } catch (e) {
      error.innerHTML = "Oops, there was an error. Please try again.";
      submit.innerHTML = submitText;
    }
  }
}

customElements.define("klaviyo-signup", KlaviyoSignup);

/*
Accordion Item
*/

class AccordionItem extends HTMLElement {
  connectedCallback() {
    this.isFullRotation = this.dataset.fullRotation === "true";
    this.summaryOnly = this.dataset.summaryOnly === "true";
    this.startOpen = this.dataset.startOpen === "true";

    this.icon = this.querySelector("svg");
    this.details = this.querySelector("details");
    this.content = this.querySelector(".content");
    this.summary = this.querySelector("summary");

    if (this.startOpen) {
      this.setInitialOpenState();
    }

    if (this.summaryOnly) {
      this.summary.addEventListener("click", (e) => {
        e.preventDefault();
        this.animateHeight();
      });
    } else {
      this.details.addEventListener("click", (e) => {
        e.preventDefault();
        this.animateHeight();
      });
    }
  }

  setInitialOpenState() {
    this.details.open = true;
    this.content.style.maxHeight = this.content.scrollHeight + "px";

    if (this.isFullRotation) {
      this.icon.style.transform = "rotate(180deg)";
    } else {
      this.icon.style.transform = "rotate(90deg)";
    }
  }

  animateHeight() {
    if (this.details.open) {
      if (this.isFullRotation) {
        this.icon.style.transform = "rotate(0deg)";
      } else {
        this.icon.style.transform = "rotate(45deg)";
      }

      this.content.style.maxHeight = "0px";
      setTimeout(() => {
        this.details.open = false;
      }, 300);
    } else {
      this.details.open = true;
      if (this.isFullRotation) {
        this.icon.style.transform = "rotate(180deg)";
      } else {
        this.icon.style.transform = "rotate(90deg)";
      }

      this.content.style.maxHeight = this.content.scrollHeight + "px";
    }
  }
}

customElements.define("accordion-item", AccordionItem);

/*
Site Modal
*/

class SiteModal extends HTMLElement {
  connectedCallback() {
    this.modal = this.querySelector(".modal");
    this.registerEvents();
    this.defineAutoOpenRoutine();
  }

  openModal() {
    if (this.modal) {
      document.body.classList.add("overflow-hidden");
      this.modal.classList.add("active");
    }
  }

  closeModal() {
    if (this.modal) {
      document.body.classList.remove("overflow-hidden");
      this.modal.classList.remove("active");
    }
  }

  registerEvents() {
    const opens = this.querySelectorAll(".modal-open");
    const closes = this.querySelectorAll(".modal-close");
    const triggerClass = this.dataset.trigger;
    const triggers = document.querySelectorAll(triggerClass);

    if (triggers) {
      triggers.forEach((trigger) => {
        trigger.addEventListener("click", (e) => {
          const agreeError = document.querySelector(".agree-error");
          if (agreeError) {
            agreeError.classList.add("hidden");

            if (e.target.classList.contains("select-subscription")) {
              if (!document.getElementById("agreeWithSubscription").checked) {
                agreeError.classList.remove("hidden");
                return;
              }
            }
          }

          this.openModal();
        });
      });
    }

    Array.from(opens).forEach((open) => {
      open.addEventListener("click", (e) => {
        this.openModal();
      });
    });

    Array.from(closes).forEach((close) => {
      close.addEventListener("click", (e) => {
        this.closeModal();
      });
    });

    if (this.modal) {
      this.modal.addEventListener("click", (e) => {
        if (e.target === this.modal) {
          this.closeModal();
        }
      });
    }
  }

  defineAutoOpenRoutine() {
    const autoOpenTimeout = this.dataset.autoOpenTimeout;
    if (
      autoOpenTimeout &&
      !localStorage.getItem("feno-subscribe") &&
      !sessionStorage.getItem("feno-subscribe")
    ) {
      setTimeout(() => {
        this.openModal();
      }, autoOpenTimeout);
    }
  }
}

customElements.define("site-modal", SiteModal);

/*
Tabs Content
*/

class TabsContent extends HTMLElement {
  connectedCallback() {
    this.tabButtons = this.querySelectorAll(".tab-button[data-tab-index]");
    this.contentBlocks = this.querySelectorAll("#contents > div");

    if (!this.tabButtons.length || !this.contentBlocks.length) return;

    this.bindEvents();
    this.setActiveTab(0);
  }

  bindEvents() {
    this.tabButtons.forEach((button) => {
      const index = parseInt(button.dataset.tabIndex, 10);
      if (!isNaN(index)) {
        button.addEventListener("click", () => this.setActiveTab(index));
      }
    });
  }

  setActiveTab(activeIndex) {
    this.contentBlocks.forEach((block, i) => {
      const isActive = i === activeIndex;
      block.classList.toggle("absolute", !isActive);
      block.classList.toggle("invisible", !isActive);
      block.classList.toggle("opacity-0", !isActive);
    });

    this.tabButtons.forEach((button) => {
      const buttonIndex = parseInt(button.dataset.tabIndex, 10);
      button.classList.toggle("active", buttonIndex === activeIndex);
    });
  }
}

customElements.define("tabs-content", TabsContent);

